// PERMANENT: Our Story page — dedicated standalone layout. Do not remove.
import { useEffect, useState } from "react";
import { Compass, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import ShareSection from "@/components/ShareSection";
import {
  BODY_CONTAINER,
  BODY_SCOPED_CSS,
  renderPlainTextArticle,
} from "@/lib/articleBody";

const STORY_SLUG = "why-we-built-summarymaps-and-where-were-going";

export default function OurStoryPage() {
  const [article, setArticle] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get(`/articles/${STORY_SLUG}`)
      .then(({ data }) => setArticle(data))
      .catch(() => setError(true));
  }, []);

  const title = article?.title || "Why We Built SummaryMaps — And Where We're Going";
  const desc =
    article?.meta_description ||
    "The story behind SummaryMaps — how we built a home for people who love to read, and where we're going next.";
  const body = article?.content || "";
  const isHtml = /<[a-z][\s\S]*>/i.test(body);

  return (
    <div className="min-h-screen topo-bg" data-testid="our-story-page">
      <SeoHead
        title={title}
        description={desc}
        image={article?.cover_image_url}
        type="article"
        isArticle
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "Our Story — SummaryMaps",
            "url": "https://summarymapsjournal.com/why-we-built-summarymaps",
            "description": desc,
            "publisher": {
              "@type": "Organization",
              "name": "SummaryMaps Journal",
              "logo": {
                "@type": "ImageObject",
                "url": "https://summarymapsjournal.com/logo.png",
              },
            },
          },
        ]}
      />


      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "radial-gradient(1200px 500px at 20% -10%, rgba(201,169,97,0.18), transparent 60%), radial-gradient(900px 500px at 90% 10%, rgba(201,169,97,0.08), transparent 60%), linear-gradient(135deg, #1B2F5E 0%, #14224a 60%, #0f1a3a 100%)",
        }}
        data-testid="story-hero"
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22160%22 height=%22160%22 viewBox=%220 0 160 160%22><g fill=%22none%22 stroke=%22%23C9A96E%22 stroke-width=%220.6%22><circle cx=%2280%22 cy=%2280%22 r=%2270%22/><circle cx=%2280%22 cy=%2280%22 r=%2250%22/><circle cx=%2280%22 cy=%2280%22 r=%2230%22/><line x1=%2280%22 y1=%220%22 x2=%2280%22 y2=%22160%22/><line x1=%220%22 y1=%2280%22 x2=%22160%22 y2=%2280%22/><line x1=%2223%22 y1=%2223%22 x2=%22137%22 y2=%22137%22/><line x1=%22137%22 y1=%2223%22 x2=%2223%22 y2=%22137%22/></g></svg>')",
            backgroundSize: "220px 220px",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 sm:px-8 pt-24 sm:pt-32 pb-16 sm:pb-24 text-center">
          <div className="inline-flex items-center gap-2 mb-6 sm:mb-8">
            <span
              className="inline-flex items-center justify-center w-10 h-10 rounded-full"
              style={{ background: "rgba(201,169,97,0.18)", color: "#C9A96E" }}
            >
              <Compass className="w-5 h-5" />
            </span>
            <span
              className="text-[11px] sm:text-xs uppercase tracking-[0.28em] font-semibold"
              style={{ color: "#C9A96E" }}
              data-testid="story-eyebrow"
            >
              Our Story
            </span>
          </div>
          <h1
            className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] font-semibold"
            data-testid="story-title"
          >
            {title}
          </h1>
          <p className="mt-6 sm:mt-8 text-white/70 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            A quiet field guide to how SummaryMaps came to be — and the reading life we're trying to keep.
          </p>
          <div
            className="mx-auto mt-10 sm:mt-14 h-px w-24"
            style={{ background: "linear-gradient(90deg, transparent, #C9A96E, transparent)" }}
          />
        </div>
      </section>

      {/* BODY */}
      <main className="relative">
        <article
          className="sm-article-body"
          style={{ ...BODY_CONTAINER, padding: "3.5rem 1.5rem 2rem" }}
          data-testid="story-body"
        >
          <style>{BODY_SCOPED_CSS}</style>
          {error ? (
            <p style={{ color: "#5C5344" }}>
              We couldn't load this story right now. Please try again in a moment.
            </p>
          ) : !article ? (
            <>
              <div className="h-4 w-40 bg-ink/5 rounded mb-6 animate-pulse" />
              <div className="h-4 w-full bg-ink/5 rounded mb-3 animate-pulse" />
              <div className="h-4 w-11/12 bg-ink/5 rounded mb-3 animate-pulse" />
              <div className="h-4 w-3/4 bg-ink/5 rounded mb-3 animate-pulse" />
            </>
          ) : isHtml ? (
            <div dangerouslySetInnerHTML={{ __html: body }} />
          ) : (
            renderPlainTextArticle(body, { skipByline: true })
          )}

          <ShareSection
            title={title}
            url="https://summarymapsjournal.com/why-we-built-summarymaps"
            testid="story-share"
          />
        </article>

        {/* CTA */}
        <section
          className="max-w-4xl mx-auto px-6 sm:px-8 pb-24 sm:pb-32 pt-8 sm:pt-12"
          data-testid="story-cta-section"
        >
          <a
            href="https://summarymaps.com"
            target="_blank"
            rel="noreferrer"
            className="group block"
            data-testid="story-cta"
          >
            <div
              className="rounded-2xl sm:rounded-3xl px-6 sm:px-12 py-10 sm:py-14 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 transition-transform hover:-translate-y-0.5"
              style={{
                background: "linear-gradient(135deg, #1B2F5E 0%, #14224a 100%)",
                border: "1px solid rgba(201, 169, 97, 0.3)",
                boxShadow: "0 30px 60px -25px rgba(27, 47, 94, 0.5)",
              }}
            >
              <div>
                <div
                  className="text-[11px] sm:text-xs uppercase tracking-[0.22em] mb-3 font-semibold"
                  style={{ color: "#C9A96E" }}
                >
                  Explore SummaryMaps
                </div>
                <div className="font-serif text-2xl sm:text-3xl lg:text-4xl text-white leading-tight">
                  Ready to map your next book?
                </div>
                <div className="mt-2.5 text-sm sm:text-base text-white/70 max-w-md">
                  Turn any book into a beautiful mind map in ninety seconds — free to try.
                </div>
              </div>
              <span
                className="inline-flex items-center gap-2 px-6 sm:px-7 py-3 sm:py-3.5 rounded-full text-sm sm:text-base font-medium whitespace-nowrap self-start sm:self-auto transition-transform group-hover:translate-x-0.5"
                style={{ background: "#C9A96E", color: "#1B2F5E" }}
              >
                Try SummaryMaps free
                <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </a>
        </section>
      </main>

    </div>
  );
}
